import { useEffect, useRef, useState } from 'react';
import { IconAlertTriangle, IconAnchor, IconCheck, IconCamera, IconMapPin, IconPhone, IconSend, IconSnowflakeOff, IconTool, IconTruck } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { PageHeader } from '@hana/shell';
import { Modal, StateView, useToast, type ScreenState } from '@hana/ui';
import { DRIVER, TRIPS } from './trips';
import { MINOR, P0S, minorValid, timeline, type Incident, type P0Id } from './incidentsData';
import './incidents.i18n';
import s from './incidents.module.css';

const ICON: Record<P0Id, typeof IconTool> = { acc: IconAlertTriangle, brk: IconTool, port: IconAnchor, rf: IconSnowflakeOff };
const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
const TRIP = TRIPS.today[0]!;

function forcedState(): ScreenState {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
}

function nowHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Mint-Mov-Phone2 khung "Báo sự cố": một chạm P0 + sự cố không khẩn (giả lập, giữ trong bộ nhớ). */
export function Incidents() {
  const t = useT();
  const state = forcedState();
  const toast = useToast();
  const [inc, setInc] = useState<Incident | null>(null);
  const [minor, setMinor] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sent, setSent] = useState<{ k: string; d: string }[]>([]);
  const ackTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(ackTimer.current), []);

  const sendP0 = (id: P0Id, label: string) => {
    const next: Incident = { id, label, time: nowHM(), ack: false };
    setInc(next);
    window.clearTimeout(ackTimer.current);
    ackTimer.current = window.setTimeout(() => setInc((c) => (c && c.id === id ? { ...c, ack: true } : c)), 3000);
    toast({ msg: t('mvi.p0sent', { l: label, d: DRIVER.duty }), undo: () => { window.clearTimeout(ackTimer.current); setInc(null); } });
  };
  const sendMinor = () => {
    if (!minor || !minorValid(text)) return;
    setSent((l) => [{ k: minor, d: `${nowHM()} · ${text.trim()}` }, ...l]);
    setMinor(null);
    setText('');
    toast({ msg: t('mvi.sent', { k: minor }) });
  };

  return (
    <>
      <PageHeader title={t('mvi.title')} />
      <div className={s.page}>
        <div className={s.meta}>
          <span title={t('mvi.duty', { d: DRIVER.duty })} aria-label={t('mvi.duty', { d: DRIVER.duty })}><IconPhone size={15} stroke={1.8} aria-hidden="true" /><b>{DRIVER.duty}</b></span>
          <span title={t('mvi.gps')} aria-label={t('mvi.gps')}><IconMapPin size={15} stroke={1.8} aria-hidden="true" />GPS</span>
          <span title={t('mvi.trip', { n: TRIP.no })} aria-label={t('mvi.trip', { n: TRIP.no })}><IconTruck size={15} stroke={1.8} aria-hidden="true" />{TRIP.no}</span>
        </div>
        {state === 'error' && <div role="status" className={s.net}><b>{t('mvi.offB')}</b> {t('mvi.offT')}</div>}
        {state === 'noperm' && <div className={s.note}>{t('mvi.np')}</div>}
        <StateView
          state={state === 'error' || state === 'noperm' || state === 'empty' ? 'ready' : state}
          skeleton={<div className={s.grid} aria-hidden="true">{[0, 1, 2, 3].map((i) => <div key={i} className={s.sk} />)}</div>}
        >
          {inc && (
            <div role="alert" className={s.active}>
              <div className={s.activeHd}><span className={s.p0}>P0</span><b>{inc.label}</b><span className={s.lb}>{inc.time}</span></div>
              <div className={s.sub}>{TRIP.no} · {DRIVER.plate} · {t('mvi.autoGps')}</div>
              <div>
                {timeline(inc, DRIVER.duty).map((st) => (
                  <div key={st.label} className={s.step}>
                    <span className={s.dot} data-on={st.on ? '1' : '0'} data-first={st.first ? '1' : '0'} />
                    <span className={s.stepTx} data-on={st.on ? '1' : '0'}>{st.label}</span>
                    <span className={s.lb}>{st.time}</span>
                  </div>
                ))}
              </div>
              <a className={s.call} href={`tel:${DRIVER.dutyPhone}`}><IconPhone size={20} stroke={2} aria-hidden="true" />{t('mvi.call', { d: DRIVER.duty })}</a>
              <div className={s.two}>
                <button type="button" className={s.ghost} onClick={() => toast({ msg: t('mvi.noPhoto') })}><IconCamera size={16} stroke={1.8} aria-hidden="true" />{t('mvi.addPhoto')}</button>
                <button type="button" className={s.ghost} onClick={() => { setInc(null); toast({ msg: t('mvi.closed') }); }}><IconCheck size={16} stroke={1.8} aria-hidden="true" />{t('mvi.done')}</button>
              </div>
            </div>
          )}
          <div className={s.cap}>{t('mvi.urgent')}</div>
          <div className={s.grid}>
            {P0S.map((p) => {
              const I = ICON[p.id];
              return <button key={p.id} type="button" className={s.p0btn} onClick={() => sendP0(p.id, p.label)}><I size={36} stroke={1.9} aria-hidden="true" />{p.label}</button>;
            })}
          </div>
          <div className={s.cap}>{t('mvi.minor')}</div>
          <div className={s.list}>
            {MINOR.map((k) => <button key={k} type="button" className={s.item} onClick={() => setMinor(k)}><span>{k}</span><span className={s.chev} aria-hidden="true">›</span></button>)}
          </div>
          {sent.map((m) => <div key={m.d} className={s.sentCard}><b>{m.k}</b><div className={s.sub}>{m.d}</div></div>)}
        </StateView>
      </div>
      <Modal
        open={minor !== null}
        onOpenChange={(o) => { if (!o) setMinor(null); }}
        title={minor ?? ''}
        footer={<button type="button" className={s.send} disabled={!minorValid(text)} onClick={sendMinor}><IconSend size={16} stroke={1.8} aria-hidden="true" />{t('mvi.send')}</button>}
      >
        <label className={s.field}>
          <span className={s.lb}>{t('mvi.desc')}</span>
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('mvi.ph')} />
        </label>
      </Modal>
    </>
  );
}
